import * as ChargebeeUpsellService from 'api/chargebee/chargebeeService.js';
import { cloneDeep } from 'lodash';
import {
    applyDiscount,
    calculateDiscount,
    findItemById,
    getLabel,
    getNameKeyAndStatus,
    getPlanDatas,
    getStatusForDiscountedItem,
    newOptionLevelItem,
    updateSubscriptionAddonsPrice,
} from '../../helpers/chargebee/chargebeeUpsellHelpers';
import {
    addAddonQuantity,
    subtractSubscriptionAddons,
} from '../../helpers/chargebee/chargebeeUpsellAddonsManager.js';

export default {
    namespaced: true,
    state() {
        return {
            subscriptionItems: [],
            previousSubscriptionItems: [],
            previousDiscounts: [],
            chargebeeCatalogue: {},
            discounts: [],
            previousPlan: null,
            customerData: {},
            paymentMethodData: {},
            subscriptionId: null,
            recurrence: '',
            subscriptionInvoicing: { heading: { price: 0, recurrence: '' }, items: [] },
            subscriptionIncoming: {},
            estimationLoading: false,
            planSelected: 'essential',
            domains: [],
            isPricingVersion3: false,
            billingCycles: null,
        };
    },
    getters: {
        getChargebeeCatalogue: (state) => state.chargebeeCatalogue,
        getSubscriptionItems: (state) => state.subscriptionItems,
        getPreviousSubscriptionItems: (state) => state.previousSubscriptionItems,
        getPreviousDiscounts: (state) => state.previousDiscounts,
        getDiscounts: (state) => state.discounts,
        getPreviousPlan: (state) => state.previousPlan,
        getCustomerData: (state) => state.customerData,
        getPaymentMethodData: (state) => state.paymentMethodData,
        getSubscriptionId: (state) => state.subscriptionId,
        getRecurrence: (state) => state.recurrence,
        getSubscriptionInvoicing: (state) => state.subscriptionInvoicing,
        getEstimationLoading: (state) => state.estimationLoading,
        getSubscriptionIncoming: (state) => state.subscriptionIncoming,
        getPlanSelected: (state) => state.planSelected,
        getDomains: (state) => state.domains,
        getIsPricingVersion3: (state) => state.isPricingVersion3,
        getBillingCycles: (state) => state.billingCycles,
    },
    mutations: {
        setChargebeeCatalogue: (state, chargebeeCatalogue) =>
            (state.chargebeeCatalogue = chargebeeCatalogue),
        setSubscriptionItems: (state, subscriptionItems) =>
            (state.subscriptionItems = subscriptionItems),
        setDiscounts: (state, discounts) => (state.discounts = discounts),
        setPreviousDiscounts: (state, discounts) => (state.previousDiscounts = discounts),
        setPreviousPlan: (state, previousPlan) => (state.previousPlan = previousPlan),
        setCustomerData: (state, customerData) => (state.customerData = customerData),
        setPaymentMethodData: (state, paymentMethodData) =>
            (state.paymentMethodData = paymentMethodData),
        setPreviousSubscriptionItems: (state, subscriptionItems) =>
            (state.previousSubscriptionItems = subscriptionItems),
        setSubscriptionId: (state, subscriptionId) => (state.subscriptionId = subscriptionId),
        setRecurrence: (state, recurrence) => (state.recurrence = recurrence),
        setSubscriptionInvoicing: (state, subscriptionInvoicing) =>
            (state.subscriptionInvoicing = subscriptionInvoicing),
        setEstimationLoading: (state, loading) => (state.estimationLoading = loading),
        setSubscriptionIncoming: (state, subscriptionIncoming) =>
            (state.subscriptionIncoming = subscriptionIncoming),
        setPlanSelected: (state, planSelected) => (state.planSelected = planSelected),
        setDomains: (state, domains) => (state.domains = domains),
        setIsPricingVersion3: (state, isPricingVersion3) =>
            (state.isPricingVersion3 = isPricingVersion3),
        setBillingCycles: (state, billingCycles) => (state.billingCycles = billingCycles),
    },
    actions: {
        async fetchChargebeeCatalogue({ commit }) {
            const { cataloguePlan } = await ChargebeeUpsellService.fetchCatalogue();
            commit('setChargebeeCatalogue', cataloguePlan);
        },
        async fetchSubscription({ commit, getters, dispatch }) {
            const {
                data: { subscription, card, customer },
            } = await ChargebeeUpsellService.fetchSubscription(true);

            if (subscription.has_scheduled_changes) {
                throw new Error('', { cause: 'has_scheduled_changes' });
            }

            commit('setBillingCycles', subscription?.remaining_billing_cycles ?? null);
            commit('setDiscounts', subscription?.discounts ?? []);
            commit('setPreviousDiscounts', subscription?.discounts ?? []);
            commit('setSubscriptionId', subscription?.id);
            commit('setRecurrence', subscription?.billing_period_unit);
            dispatch('updateCustomerPaymentMethodData', { card, customer });

            const isPricingVersion3 = subscription?.subscription_items?.some(
                (item) => item.item_type === 'plan' && item.item_price_id?.split('_')[0] === '3',
            );
            commit('setIsPricingVersion3', isPricingVersion3);

            const subscriptionItems = subscription.subscription_items.map((subscriptionItem) => {
                if (subscriptionItem.item_type === 'plan') {
                    commit(
                        'setPreviousPlan',
                        !isPricingVersion3
                            ? getPlanDatas(subscriptionItem.item_price_id).code
                            : '3',
                    );
                    commit(
                        'setPlanSelected',
                        !isPricingVersion3
                            ? getPlanDatas(subscriptionItem.item_price_id).code
                            : '3',
                    );
                }
                const { nameKey, status } =
                    getNameKeyAndStatus(subscriptionItem.item_price_id) ?? '';
                const discountedItem = getters.getDiscounts.find(
                    (discount) => discount.item_price_id === subscriptionItem.item_price_id,
                );

                subscriptionItem = {
                    ...subscriptionItem,
                    nameKey: nameKey,
                    status:
                        getStatusForDiscountedItem(
                            subscriptionItem.item_price_id,
                            discountedItem,
                        ) ?? status,
                    newItem: false,
                    updatedQuantity: 0,
                    ...getLabel(subscriptionItem),
                };
                subscriptionItem = applyDiscount({
                    subscriptionItem,
                    discounts: getters.getDiscounts.filter(() => discountedItem),
                });
                return subscriptionItem;
            });
            commit('setPreviousSubscriptionItems', cloneDeep(subscriptionItems));
            commit('setSubscriptionItems', subscriptionItems);
            dispatch('updateSubscriptionIncoming');
        },
        async updatePlan({ commit, getters, dispatch }, planKey) {
            commit('setEstimationLoading', true);
            commit('setPlanSelected', planKey);
            const subscriptionItems = getters.getSubscriptionItems.map((subscriptionItem) => {
                const idItem =
                    subscriptionItem.item_type === 'plan'
                        ? getters.getChargebeeCatalogue[planKey].id
                        : subscriptionItem.item_price_id;

                let itemFromCatalogue;
                //wWe have to  change discount by full into id because catalogue didn't contain discount or free addon
                const checkDiscountFree = /discount|free/i;
                if (checkDiscountFree.test(idItem)) {
                    findItemById(
                        idItem.replace(checkDiscountFree, 'full'),
                        getters.getChargebeeCatalogue[planKey],
                        (item) => (itemFromCatalogue = item),
                    );
                } else {
                    findItemById(
                        idItem,
                        getters.getChargebeeCatalogue[planKey],
                        (item) => (itemFromCatalogue = item),
                    );
                }

                if (itemFromCatalogue) {
                    const { nameKey, status } =
                        getNameKeyAndStatus(subscriptionItem.item_price_id) ?? '';

                    subscriptionItem.unit_price = itemFromCatalogue?.price;
                    subscriptionItem.amount = itemFromCatalogue?.price * subscriptionItem?.quantity;
                    if (['discount', 'free'].includes(subscriptionItem.status)) {
                        subscriptionItem = applyDiscount({
                            subscriptionItem,
                            discounts: getters.getPreviousDiscounts,
                        });
                    }
                    subscriptionItem.item_price_id = idItem;
                    return {
                        ...subscriptionItem,
                        nameKey: nameKey,
                        status:
                            subscriptionItem.status === 'modified'
                                ? subscriptionItem.status
                                : status,
                        newItem: false,
                        ...getLabel(subscriptionItem),
                    };
                }

                return subscriptionItem;
            });
            try {
                await dispatch('estimateSubscriptionUpdate', subscriptionItems);
                await commit('setSubscriptionItems', subscriptionItems);
                dispatch('updateSubscriptionIncoming');
            } catch {
                commit('setPlanSelected', getters.getPreviousPlan);
                throw new Error();
            } finally {
                commit('setEstimationLoading', false);
            }
        },
        async updateAddonQuantity(
            { commit, getters, dispatch },
            { addonId, quantity, isOption = false },
        ) {
            commit('setEstimationLoading', true);
            let subscriptionItems = cloneDeep(getters.getSubscriptionItems);
            const addon = subscriptionItems.find((item) => item.item_price_id === addonId);
            if (addon) {
                const addons = subscriptionItems.filter((item) => item.nameKey === addon.nameKey);
                const totalAddonsQuantity = addons.reduce(
                    (acc, record) => acc + record.quantity,
                    0,
                );
                const addedValue = quantity - totalAddonsQuantity;
                //we check if we add addon to update else we need to remove addons from discount and free first
                if (totalAddonsQuantity > quantity) {
                    subtractSubscriptionAddons({ addons, addon, addedValue });
                    if (addon.newItem && quantity === 0) {
                        subscriptionItems = subscriptionItems.filter(
                            (item) => item.item_price_id !== addon.item_price_id,
                        );
                    }
                } else {
                    let rest = addedValue;

                    if (addon.updatedQuantity < 0) {
                        rest = addAddonQuantity({
                            status: 'full',
                            addedValue: rest,
                            nameKey: addon.nameKey,
                            addons,
                        });
                    }

                    if (rest > 0) {
                        rest = addAddonQuantity({
                            status: 'discount',
                            addedValue: rest,
                            nameKey: addon.nameKey,
                            addons,
                        });
                    }

                    if (rest > 0) {
                        rest = addAddonQuantity({
                            status: 'free',
                            addedValue: rest,
                            nameKey: addon.nameKey,
                            addons,
                        });
                    }

                    //Once the removed licenses have been returned, you must add the complete license surplus.
                    if (rest > 0) {
                        rest = addAddonQuantity({
                            status: 'full',
                            addedValue: rest,
                            nameKey: addon.nameKey,
                            addons,
                        });
                    }
                }
            } else {
                const key = isOption ? 'options' : 'licenses';
                let addonFromDictionnary = {};
                findItemById(
                    addonId,
                    getters.getChargebeeCatalogue[getters.getPreviousPlan].addons[key],
                    (item) => (addonFromDictionnary = cloneDeep(item)),
                );

                const { nameKey, status } = getNameKeyAndStatus(addonFromDictionnary.id) ?? '';
                const addonFree = subscriptionItems.find(
                    (addon) => addon.status === 'free' && addon.nameKey === nameKey,
                );

                const oldAddonFree = getters.getPreviousSubscriptionItems.find(
                    (addon) => addon.status === 'free' && addon.nameKey === nameKey,
                );

                const addonDiscounted = subscriptionItems.find(
                    (addon) => addon.status === 'discount' && addon.nameKey === nameKey,
                );
                const oldAddonDiscounted = getters.getPreviousSubscriptionItems.find(
                    (addon) => addon.status === 'discount' && addon.nameKey === nameKey,
                );

                let totalQuantityDiscountFree =
                    (addonFree?.quantity ?? 0) + (addonDiscounted?.quantity ?? 0);

                if (quantity === 0) {
                    if (addonDiscounted) {
                        addonDiscounted.quantity = 0;
                        addonDiscounted.updatedQuantity = -oldAddonDiscounted?.quantity;
                    }
                    if (addonFree) {
                        addonFree.quantity = 0;
                        addonFree.updatedQuantity = -oldAddonFree?.quantity;
                    }
                }

                const incrementQuantity = (addon, oldAddon) => {
                    while (addon.quantity < oldAddon.quantity && quantity > 0) {
                        addon.quantity++;
                        addon.updatedQuantity = addon.quantity - oldAddon.quantity;
                        quantity--;
                    }
                };
                const decrementQuantity = (addon, oldAddon) => {
                    while (addon.quantity > 0 && quantity !== totalQuantityDiscountFree) {
                        addon.quantity--;
                        addon.updatedQuantity = addon.quantity - oldAddon.quantity;
                        totalQuantityDiscountFree--;
                    }
                };

                if (totalQuantityDiscountFree < quantity) {
                    if (addonFree) {
                        incrementQuantity(addonFree, oldAddonFree);
                    }
                    if (addonDiscounted) {
                        incrementQuantity(addonDiscounted, oldAddonDiscounted);
                    }
                } else {
                    if (addonFree) {
                        decrementQuantity(addonFree, oldAddonFree);
                    }
                    if (addonDiscounted) {
                        decrementQuantity(addonDiscounted, oldAddonDiscounted);
                    }
                }
                quantity -= totalQuantityDiscountFree;
                if (quantity > 0) {
                    addonFromDictionnary = {
                        ...addonFromDictionnary,
                        nameKey: nameKey,
                        status: status,
                        updatedQuantity: quantity,
                        item_price_id: addonFromDictionnary.id,
                        quantity: quantity,
                        newItem: true,
                        unit_price: addonFromDictionnary.price,
                        amount: quantity * addonFromDictionnary.price,
                        ...getLabel(addonFromDictionnary),
                    };
                    subscriptionItems = [...subscriptionItems, addonFromDictionnary];
                }
            }

            try {
                subscriptionItems = updateSubscriptionAddonsPrice({
                    subscriptionItems,
                    discounts: getters.getDiscounts,
                });

                await dispatch('estimateSubscriptionUpdate', subscriptionItems);

                await commit('setSubscriptionItems', subscriptionItems);
                dispatch('updateSubscriptionIncoming');
            } catch (e) {
                throw new Error(e);
            } finally {
                commit('setEstimationLoading', false);
            }
        },
        async updateOptionStatus({ commit, getters, state, dispatch }, { optionNameKey, value }) {
            commit('setEstimationLoading', true);
            const optionIsInSubscription = getters.getSubscriptionItems.find(
                (option) => option.nameKey === optionNameKey,
            );
            let subscriptionItems = cloneDeep(getters.getSubscriptionItems);
            //if option is not in subscription we need to add it
            if (!optionIsInSubscription) {
                let option =
                    getters.getChargebeeCatalogue[state.previousPlan].addons?.options[
                        optionNameKey
                    ];
                if (!option) {
                    throw new Error();
                }
                if (option?.length > 1) {
                    option = option.find((item) => item.id.includes('starter'));
                }
                subscriptionItems = subscriptionItems.concat({
                    amount: option.price,
                    newItem: true,
                    item_price_id: option.id,
                    item_type: 'addon',
                    nameKey: optionNameKey,
                    quantity: 1,
                    updatedQuantity: 1,
                    status: 'full',
                    unit_price: option.price,
                    ...getLabel(option),
                });
            } else {
                const { status: oldStatus, item_price_id: oldItemPriceId } = cloneDeep(
                    getters.getPreviousSubscriptionItems.find(
                        (option) => option.nameKey === optionNameKey,
                    ) ?? {},
                );
                if (
                    optionIsInSubscription.status === 'full' &&
                    optionIsInSubscription.updatedQuantity === 1
                ) {
                    subscriptionItems = subscriptionItems.filter(
                        (item) => item.item_price_id !== optionIsInSubscription.item_price_id,
                    );
                }
                let status = value ? 'modified' : 'removed';
                if (optionIsInSubscription.item_price_id === oldItemPriceId) {
                    status = value ? oldStatus : 'removed';
                }
                subscriptionItems = subscriptionItems.map((item) => {
                    if (item.nameKey === optionNameKey) {
                        item.quantity = value ? 1 : 0;
                        item.updatedQuantity = value ? 0 : -1;
                        item.status = status;
                    }
                    return item;
                });
            }
            const subscriptionItemsBeforeEstimate = cloneDeep(subscriptionItems);

            try {
                await dispatch('estimateSubscriptionUpdate', subscriptionItems);
                subscriptionItems = updateSubscriptionAddonsPrice({
                    subscriptionItems,
                    discounts: getters.getDiscounts,
                });
                await commit('setSubscriptionItems', subscriptionItems);

                dispatch('updateSubscriptionIncoming');
            } catch (e) {
                commit('setSubscriptionItems', subscriptionItemsBeforeEstimate);
                console.error(e);
                throw new Error();
            } finally {
                commit('setEstimationLoading', false);
            }
        },
        async removeAddonByNamekey({ commit, getters, dispatch }, optionNameKey) {
            let subscriptionItems = cloneDeep(getters.getSubscriptionItems);
            const subscriptionItemsBeforeEstimate = cloneDeep(subscriptionItems);

            commit('setEstimationLoading', true);
            subscriptionItems = subscriptionItems.filter((item) => {
                return item.nameKey !== optionNameKey;
            });

            try {
                commit('setSubscriptionItems', subscriptionItems);
                await dispatch('estimateSubscriptionUpdate', subscriptionItems);
                subscriptionItems = updateSubscriptionAddonsPrice({
                    subscriptionItems,
                    discounts: getters.getDiscounts,
                });
                await commit('setSubscriptionItems', subscriptionItems);
                dispatch('updateSubscriptionIncoming');
            } catch (e) {
                console.error(e);
                commit('setSubscriptionItems', subscriptionItemsBeforeEstimate);
                throw new Error();
            } finally {
                commit('setEstimationLoading', false);
            }
        },
        async updateOptionLevel({ commit, getters, dispatch }, { optionNameKey, option }) {
            commit('setEstimationLoading', true);
            let subscriptionItems = cloneDeep(getters.getSubscriptionItems);
            //We want to keep previous state of subscriptionItems if estimation fail
            const subscriptionItemsBeforeEstimate = cloneDeep(subscriptionItems);

            const subscriptionItemUpdated = subscriptionItems.find(
                (item) => item.nameKey === optionNameKey,
            );
            //used when we select an option that was already in subscription
            const previousSubscriptionItemUpdated = getters.getPreviousSubscriptionItems.find(
                (item) => item.item_price_id === option.id,
            );

            let newOption = {};

            if (previousSubscriptionItemUpdated) {
                newOption = {
                    ...previousSubscriptionItemUpdated,
                    updatedQuantity: subscriptionItemUpdated.updatedQuantity,
                };
            } else {
                newOption = newOptionLevelItem(
                    {
                        ...option,
                        nameKey: optionNameKey,
                        newItem: subscriptionItemUpdated?.newItem ?? true,
                    },
                    getters.getAddonsLabels,
                );
            }

            subscriptionItems = subscriptionItems.filter((item) => item.nameKey !== optionNameKey);
            subscriptionItems = subscriptionItems.concat(newOption);
            try {
                commit('setSubscriptionItems', subscriptionItems);
                await dispatch('estimateSubscriptionUpdate', subscriptionItems);
                subscriptionItems = updateSubscriptionAddonsPrice({
                    subscriptionItems,
                    discounts: getters.getDiscounts,
                });
                await commit('setSubscriptionItems', subscriptionItems);
                dispatch('updateSubscriptionIncoming');
            } catch (e) {
                console.error(e);
                commit('setSubscriptionItems', subscriptionItemsBeforeEstimate);
                throw new Error();
            } finally {
                commit('setEstimationLoading', false);
            }
        },
        //This method is used to update pack marketing email level and add discount related if needed
        async updatePackMarketingEmailLevelAndDiscounts(
            { getters, commit, dispatch },
            { optionNameKey, newSubOption },
        ) {
            commit('setEstimationLoading', true);
            let subscriptionItems = cloneDeep(getters.getSubscriptionItems);
            const subscriptionItemsBeforeEstimate = cloneDeep(subscriptionItems);
            const discounts = cloneDeep(getters.getDiscounts);
            const packsMarketingEmailOnSubscription = subscriptionItems.filter(
                (item) => item.nameKey === optionNameKey,
            );
            for (const packsMarketingEmailOnSubscriptionElement of packsMarketingEmailOnSubscription) {
                const discount = discounts.find(
                    (discount) =>
                        discount.item_price_id ===
                        packsMarketingEmailOnSubscriptionElement.item_price_id,
                );
                let newItemPriceId = newSubOption.id;
                newItemPriceId = newItemPriceId.replace(
                    'full',
                    packsMarketingEmailOnSubscriptionElement.status,
                );
                packsMarketingEmailOnSubscriptionElement.unit_price = newSubOption.price;
                packsMarketingEmailOnSubscriptionElement.item_price_id = newItemPriceId;

                const newSubOptionAlreadyInCurrentSubscription =
                    getters.getPreviousSubscriptionItems.some(
                        (item) => item.item_price_id === newItemPriceId,
                    );

                if (discount) {
                    discount.item_price_id = newItemPriceId;
                    discount.shouldReplace = !newSubOptionAlreadyInCurrentSubscription;
                }
            }
            try {
                commit('setSubscriptionItems', subscriptionItems);
                await commit('setDiscounts', discounts);
                await dispatch('estimateSubscriptionUpdate', subscriptionItems);
                subscriptionItems = updateSubscriptionAddonsPrice({
                    subscriptionItems,
                    discounts,
                });
                await commit('setSubscriptionItems', subscriptionItems);
                dispatch('updateSubscriptionIncoming');
            } catch (e) {
                console.error(e);
                commit('setSubscriptionItems', subscriptionItemsBeforeEstimate);
                commit('setDiscounts', getters.getPreviousDiscounts);
                throw new Error();
            } finally {
                commit('setEstimationLoading', false);
            }
        },
        async estimateSubscriptionUpdate({ commit, getters, dispatch }, subscriptionItems) {
            const data = {
                id: getters.getSubscriptionId,
                items_prices: subscriptionItems,
                discounts: getters.getDiscounts,
            };
            try {
                const {
                    data: { estimate },
                } = await ChargebeeUpsellService.fetchSubscriptionEstimation(data);
                await dispatch('updateSubscriptionInvoicing', estimate);
            } catch (e) {
                console.error(e);
                throw new Error();
            } finally {
                commit('setEstimationLoading', false);
            }
        },
        updateSubscriptionIncoming({ commit, getters }) {
            const totalAmount = getters.getSubscriptionItems.reduce(
                (sum, record) => sum + record.amount,
                0,
            );
            let totalDiscount = 0;
            for (const subscriptionItem of getters.getSubscriptionItems) {
                totalDiscount += calculateDiscount({
                    subscriptionItem,
                    discounts: getters.getDiscounts,
                });
            }
            const subscriptionIncoming = {
                heading: {
                    price: totalAmount,
                    recurrence: getters.getRecurrence,
                },
                items: [
                    {
                        label: window.i18n.__('Réduction appliquée'),
                        recurrence: getters.getRecurrence,
                        price: totalDiscount * -1,
                    },
                ],
            };
            if (getters.getSubscriptionInvoicing?.item) {
                subscriptionIncoming.items[1] = {
                    label: window.i18n.__(`TVA ${getters.getSubscriptionInvoicing.item.rate}%`),
                    price:
                        (subscriptionIncoming.heading.price *
                            getters.getSubscriptionInvoicing.item.rate) /
                        100,
                };
            }
            commit('setSubscriptionIncoming', subscriptionIncoming);
        },
        updateSubscriptionInvoicing({ commit, getters }, estimate) {
            const invoiceEstimateExists = estimate?.invoice_estimate;
            const invoiceEstimate = invoiceEstimateExists ?? estimate?.next_invoice_estimate;

            if (invoiceEstimate?.taxes.length > 0) {
                const invoicingTaxesRate = invoiceEstimate.line_item_taxes[0].tax_rate;
                const subscriptionInvoicing = getters.getSubscriptionInvoicing;
                const creditsFromCreditNote =
                    estimate.credit_note_estimates?.reduce((acc, creditNote) => {
                        return acc + creditNote.amount_allocated;
                    }, 0) ?? 0;
                let promotionalCreditsAmount = invoiceEstimate.credits_applied;
                if (invoiceEstimate.discounts?.length > 0) {
                    promotionalCreditsAmount += invoiceEstimate.discounts.find(
                        (discount) => discount.entity_type === 'promotional_credits',
                    )?.amount;
                }
                subscriptionInvoicing.heading.price = invoiceEstimateExists
                    ? invoiceEstimate.amount_due
                    : 0;
                subscriptionInvoicing.items[0] = {
                    label: window.i18n.__('Crédit appliqué'),
                    price: invoiceEstimateExists
                        ? (promotionalCreditsAmount - creditsFromCreditNote) * -1
                        : 0,
                };
                subscriptionInvoicing.items[1] = {
                    label: window.i18n.__(`TVA ${invoicingTaxesRate}%`),
                    rate: invoicingTaxesRate,
                    price: invoiceEstimateExists
                        ? (invoiceEstimate.amount_due / (100 + invoicingTaxesRate)) *
                          invoicingTaxesRate
                        : 0,
                };

                commit('setSubscriptionInvoicing', subscriptionInvoicing);
            }
        },
        updateCustomerPaymentMethodData({ commit }, { card, customer }) {
            commit('setCustomerData', customer);
            if (card) {
                commit('setPaymentMethodData', card);
            } else if (customer.payment_method.type === 'direct_debit') {
                commit('setPaymentMethodData', {
                    paymentMethodType: customer.payment_method.type,
                });
            }
        },
        updateDomain({ commit, getters }, domain) {
            const existingDomain = getters.getDomains.find((item) => item.hash === domain.hash);
            if (existingDomain) {
                Object.assign(existingDomain, domain);
            } else {
                getters.getDomains.push(domain);
            }
            commit('setDomains', getters.getDomains);
        },
        removeDomain({ commit, getters }, domain) {
            commit(
                'setDomains',
                getters.getDomains.filter((item) => item.hash !== domain.hash),
            );
        },
    },
};
